/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  buildSnapBannerViewAnalytics,
  buildSnapBannerTapAnalytics,
  buildTripTypeTabAnalytics,
  buildSnapSearchWidgetViewAnalytics,
  buildSnapSearchButtonClickAnalytics,
  buildSnapLastSearchedViewedAnalytics,
  buildSnapLastSearchedClickedAnalytics,
  buildSnapChinaDisclaimerViewedAnalytics,
} from './snapSearchWidget.analytics';
import {
  BannerViewParams,
  BannerTapParams,
  TripTypeTabParams,
  SnapSearchButtonParams,
  SnapLastSearchedViewedParams,
  SnapLastSearchedClickedParams,
  SnapChinaDisclaimerViewedParams,
} from './snapSearchWidget.meta';

/**
 * SNAP Banner Events
 */

/**
 * SNAP banner view event - fires when a banner becomes visible in the carousel
 * @param params - Banner view parameters (bannerName, position)
 * @returns Analytics parameters
 */
export const snapBannerViewEvent = (params: BannerViewParams) => {
  return buildSnapBannerViewAnalytics(params);
};

/**
 * SNAP banner tap event - fires when a user taps/clicks a banner
 * @param params - Banner tap parameters (bannerName, position)
 * @returns Analytics parameters
 */
export const snapBannerTapEvent = (params: BannerTapParams) => {
  return buildSnapBannerTapAnalytics(params);
};

/**
 * Trip Type Tab Events
 */

/**
 * Trip type tab tap event - fires when user taps roundtrip/oneway tab
 * @param params - Trip type tab parameters (tripType)
 * @returns Analytics parameters
 */
export const tripTypeTabEvent = (params: TripTypeTabParams) => {
  return buildTripTypeTabAnalytics(params);
};

/**
 * Search Widget Events
 */

/**
 * SNAP search widget view event - fires when user sees the SNAP search widget page
 * @returns Analytics parameters
 */
export const snapSearchWidgetViewEvent = () => {
  return buildSnapSearchWidgetViewAnalytics();
};

/**
 * SNAP search button click event - fires when user clicks the search button
 * @param params - Search button click parameters
 * @returns Analytics parameters
 */
export const snapSearchButtonClickEvent = (params: SnapSearchButtonParams) => {
  return buildSnapSearchButtonClickAnalytics(params);
};

/**
 * SNAP last searched viewed event - fires when recent searches are exposed to user
 * @param params - Last searched viewed parameters
 * @returns Analytics parameters
 */
export const snapLastSearchedViewedEvent = (
  params: SnapLastSearchedViewedParams,
) => {
  return buildSnapLastSearchedViewedAnalytics(params);
};

/**
 * SNAP last searched clicked event - fires when user clicks on a recent search
 * @param params - Last searched clicked parameters
 * @returns Analytics parameters
 */
export const snapLastSearchedClickedEvent = (
  params: SnapLastSearchedClickedParams,
) => {
  return buildSnapLastSearchedClickedAnalytics(params);
};

/**
 * SNAP China disclaimer viewed event - fires when user views China disclaimer message
 * @param params - China disclaimer viewed parameters
 * @returns Analytics parameters
 */
export const snapChinaDisclaimerViewedEvent = (
  params: SnapChinaDisclaimerViewedParams,
) => {
  return buildSnapChinaDisclaimerViewedAnalytics(params);
};
